import type { SupabaseClient } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { requireLiveSession } from "@/lib/requireLiveSession";

/**
 * The org setup wizard's two bookkeeping keys, and the only client path that
 * writes them (#787).
 *
 *   onboardingStep         furthest step reached, so a reopened wizard resumes
 *   onboardingDismissedAt  when the admin last dismissed it (auto-open snooze)
 *
 * They live in `organizations.profile_data`, but they are not profile content.
 * They used to go through `saveOrgProfilePatch`, and every such write advanced
 * `organizations.updated_at` and stamped `profile_updated_by`. Closing the
 * auto-opened wizard therefore invalidated the compare-and-swap expectation of
 * any editor open on the same organization and named whoever closed it as the
 * profile's last editor. `save_org_onboarding_bookmark` (migration
 * 20260930000000) writes only these two keys — any other key is refused — and
 * leaves `updated_at` and `profile_updated_by` exactly as they were, so it is
 * invisible to the editor's compare-and-swap and to its attribution.
 *
 * Never send these keys through `saveOrgProfilePatch`, and never send anything
 * else through here.
 *
 * ── TODO: delete the schema extension below once types are regenerated ──────
 * Same situation as src/lib/notificationsRpc.ts: the generated
 * `Database["public"]["Functions"]` has no key for this function until
 * `supabase gen types typescript` is re-run against an environment carrying the
 * migration. `BookmarkFunctions` states the signature as the generator would;
 * `db` is the same client instance described with it added.
 */

export interface OrgOnboardingBookmark {
  /** Whole number, 1–20 server-side; the wizard sends 1..TOTAL_STEPS. */
  onboardingStep?: number;
  /** ISO timestamp string. */
  onboardingDismissedAt?: string;
}

export type OrgOnboardingBookmarkResult =
  | { status: "saved" }
  | { status: "failed"; message: string };

type BookmarkFunctions = {
  save_org_onboarding_bookmark: {
    Args: { p_org_id: string; p_bookmark: OrgOnboardingBookmark };
    Returns: {
      updated_at: string;
      onboardingStep: number | null;
      onboardingDismissedAt: string | null;
    };
  };
};

type BookmarkDatabase = Omit<Database, "public"> & {
  public: Omit<Database["public"], "Functions"> & {
    Functions: Database["public"]["Functions"] & BookmarkFunctions;
  };
};

const db: SupabaseClient<BookmarkDatabase> = supabase;

/** The only keys the server accepts; anything else is dropped before sending. */
const pickBookmark = (bookmark: OrgOnboardingBookmark): OrgOnboardingBookmark => {
  const out: OrgOnboardingBookmark = {};
  if (bookmark.onboardingStep !== undefined) out.onboardingStep = bookmark.onboardingStep;
  if (bookmark.onboardingDismissedAt !== undefined) {
    out.onboardingDismissedAt = bookmark.onboardingDismissedAt;
  }
  return out;
};

const runSave = async (
  orgId: string,
  bookmark: OrgOnboardingBookmark,
): Promise<OrgOnboardingBookmarkResult> => {
  try {
    const payload = pickBookmark(bookmark);
    if (Object.keys(payload).length === 0) {
      return { status: "failed", message: "Empty onboarding bookmark" };
    }
    // Never send an authenticated-only call with the publishable key (#794).
    await requireLiveSession();
    const { error } = await db.rpc("save_org_onboarding_bookmark", {
      p_org_id: orgId,
      p_bookmark: payload,
    });
    if (error) {
      console.error("[orgOnboardingBookmark] save_org_onboarding_bookmark failed", error);
      return { status: "failed", message: error.message };
    }
    return { status: "saved" };
  } catch (err) {
    return { status: "failed", message: err instanceof Error ? err.message : String(err) };
  }
};

/**
 * Per-org serialization, as in saveOrgProfilePatch: two step bookmarks from one
 * tab (step 2, then step 3) land in call order, so the resume point cannot
 * regress because the requests raced.
 */
const chains = new Map<string, Promise<OrgOnboardingBookmarkResult>>();

/** Write the wizard's bookmark keys. Never rejects; resolves `failed` on any error. */
export const saveOrgOnboardingBookmark = (
  orgId: string,
  bookmark: OrgOnboardingBookmark,
): Promise<OrgOnboardingBookmarkResult> => {
  const prior = chains.get(orgId) ?? Promise.resolve<OrgOnboardingBookmarkResult | null>(null);
  const run = prior.then(
    () => runSave(orgId, bookmark),
    () => runSave(orgId, bookmark),
  );
  chains.set(orgId, run);
  return run;
};
